import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  Users,
  CalendarClock,
  ClipboardCheck,
  FileText,
  Building2,
  Settings,
  BarChart3,
  Search,
  Menu,
  X,
  Sun,
  Moon,
  Mail,
} from "lucide-react";
import { WalkInRegistration } from "@/components/WalkInRegistration";
import { GlobalSearch } from "@/components/GlobalSearch";
import { useAuth } from "@/lib/auth-context";
import { getCandidatePool, getInterviewSessions } from "@/lib/api";
import { ShieldCheck, LogOut } from "lucide-react";

const STAFFING_LINKS = [
  { to: "/dashboard", icon: LayoutGrid, label: "Staffing Home", end: true },
  { to: "/candidate-pool", icon: Users, label: "Candidate Pool" },
  { to: "/interview-sessions", icon: CalendarClock, label: "Interview Sessions" },
  { to: "/communications", icon: Mail, label: "Communications" },
  { to: "/employer-decisions", icon: ClipboardCheck, label: "Employer Decisions", disabled: true },
  { to: "/candidate-records", icon: FileText, label: "Candidate Records", disabled: true },
];

const ADMIN_LINKS = [
  { to: "/employers", icon: Building2, label: "Employers", disabled: true },
  { to: "/interview-setup", icon: Settings, label: "Interview Setup" },
  { to: "/onboarding-admin", icon: FileText, label: "Onboarding Admin" },
  { to: "/reports", icon: BarChart3, label: "Reports", disabled: true },
];

function SideLink({ item, onNavigate }) {
  const Icon = item.icon;
  const testid = `nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
  if (item.disabled) {
    return (
      <span className="side-link disabled" data-testid={testid} title="Available in a later milestone">
        <Icon className="ic" size={18} strokeWidth={2} />
        <span className="lbl">{item.label}</span>
        {item.count != null && <span className="ct">{item.count}</span>}
      </span>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={testid}
      className={({ isActive }) => `side-link${isActive ? " on" : ""}`}
    >
      <Icon className="ic" size={18} strokeWidth={2} />
      <span className="lbl">{item.label}</span>
      {item.count != null && <span className="ct">{item.count}</span>}
    </NavLink>
  );
}

function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "day";
    const urlTheme = new URLSearchParams(window.location.search).get("theme");
    if (urlTheme === "night" || urlTheme === "day") return urlTheme;
    return localStorage.getItem("mihp-theme") || "day";
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("mihp-theme", theme);
  }, [theme]);
  return [theme, () => setTheme((t) => (t === "night" ? "day" : "night"))];
}

export function AppShell({ user }) {
  const [open, setOpen] = useState(false);
  const [theme, toggleTheme] = useTheme();
  const location = useLocation();
  const { signOut } = useAuth();
  const [counts, setCounts] = useState({});
  const initials = user?.initials || (user?.name ? user.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() : "");
  const isOwner = user?.role === "owner_admin";
  const adminLinks = isOwner
    ? [...ADMIN_LINKS, { to: "/users-access", icon: ShieldCheck, label: "Users & Access" }]
    : ADMIN_LINKS;

  // Real, live navigation counts — reuse the same population the Candidate Pool
  // page shows (sum of its segments) and the count of active interview sessions.
  // No hardcoded/placeholder numbers; badges stay hidden if a fetch fails.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [pool, sessions] = await Promise.all([getCandidatePool(), getInterviewSessions()]);
        if (!alive) return;
        const poolTotal = (pool?.segments || []).reduce((a, s) => a + (s.count || 0), 0);
        const activeSessions = (sessions || []).filter((s) => s.status === "live" || s.status === "upcoming").length;
        setCounts({ "/candidate-pool": poolTotal, "/interview-sessions": activeSessions });
      } catch { /* leave badges hidden on failure — never show mock numbers */ }
    })();
    return () => { alive = false; };
  }, [location.pathname]);

  const withCounts = (links) => links.map((l) => (counts[l.to] != null ? { ...l, count: counts[l.to] } : l));

  const titles = { "/dashboard": "Staffing Home", "/candidate-pool": "Candidate Pool", "/interview-sessions": "Interview Sessions", "/communications": "Communications", "/interview-setup": "Interview Setup", "/onboarding-admin": "Onboarding Admin", "/users-access": "Users & Access" };
  let title = titles[location.pathname] || "Staffing";
  if (location.pathname.startsWith("/candidates/")) title = "Candidate Record";

  return (
    <div className="mihp-shell">
      <aside className={`sidebar${open ? " open" : ""}`} data-testid="app-sidebar">
        <div className="brand">
          <img src="/mihp-logo.png" alt="Make It Happen Philly" />
          <div className="bt">
            MAKE IT HAPPEN<span>OPERATIONS PLATFORM</span>
          </div>
          <button className="side-close" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="sidebar-close">
            <X size={20} />
          </button>
        </div>

        <nav className="nav">
          <div className="grp">Staffing</div>
          {withCounts(STAFFING_LINKS).map((l) => (
            <SideLink key={l.label} item={l} onNavigate={() => setOpen(false)} />
          ))}
          <div className="grp">Administration</div>
          {adminLinks.map((l) => (
            <SideLink key={l.label} item={l} onNavigate={() => setOpen(false)} />
          ))}
        </nav>

        <div className="who" data-testid="current-user">
          <div className="av">{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="nm">{user?.name || ""}</div>
            <div className="rl">{user?.roleLabel || user?.role || ""}</div>
          </div>
          <button className="who-signout" onClick={signOut} data-testid="sign-out-btn" title="Sign out" aria-label="Sign out">
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      {open && <div className="scrim" onClick={() => setOpen(false)} />}

      <div className="main">
        <div className="topbar">
          <button className="hamburger" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="open-menu">
            <Menu size={22} />
          </button>
          <div className="ttl" data-testid="page-title">{title}</div>
          <div className="sp" />
          <GlobalSearch />
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label="Toggle day and night mode"
            title={theme === "night" ? "Switch to Day mode" : "Switch to Night mode"}
          >
            {theme === "night" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <WalkInRegistration />
        </div>

        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
